import React from 'react';
import Moment from 'react-moment';
import axios from 'axios';
import { connect } from 'react-redux';
import { withStyles} from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { withSnackbar } from 'notistack';
import Button from '@material-ui/core/Button';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import SwapVertIcon from '@material-ui/icons/SwapVert';
import confirm from '../../shared/ConfirmDialog';
import { capitalizeFirstLetter } from '../../../helpers';

const styles = theme => ({
  root: {
    display: 'block',
    width: '100%',
    border: '1px solid #f5f5f5',
    borderRadius: 5,
    overflowX: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    '-ms-overflow-style': '-ms-autohiding-scrollbar',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& thead tr th': {
      padding: 10,
      textAlign: 'center',
      fontSize: 12,
      border: '1px solid #f5f5f5',
      cursor: 'pointer',
    },
    '& tbody tr td': {
      padding: 10,
      border: '1px solid #f5f5f5',
    },
    '& tbody tr:nth-of-type(odd) td': {
      backgroundColor: '#fafafa',
    },
  },
  tableHeadingText: {
    lineHeight: 2,
    '& b': {
      verticalAlign: 'super',
    },
  },
  textCenter: {
    textAlign: 'center',
  },
  textMute: {
    color: '#999999',
  },
});


const mapStateToProps = state => {
  return {
    selectedPupil: state.selectedPupil,
  };
};
class CovidTestsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sort: null,
      sortDirection: null,
    };

    this.columns = [
      {
        key: 'test_date',
        headingText: props.t('Date'),
        content: (entry) => {
          return (
            <React.Fragment>
              <Moment format={'ddd ' + dateFormat}>{entry.test_date}</Moment>
            </React.Fragment>
          );
        },
      },
      {
        key: 'barcode',
        headingText: props.t('Barcode'),
        content: (entry) => {
          return (
            <React.Fragment>
              {entry.barcode}
            </React.Fragment>
          );
        },
      },
      {
        key: 'symptoms',
        headingText: props.t('Symptoms'),
        content: (entry) => {
          return (
            <React.Fragment>
              {capitalizeFirstLetter(entry.symptoms)}
            </React.Fragment>
          );
        },
      },
      {
        key: 'symptoms_date',
        headingText: props.t('Symptoms since'),
        content: (entry) => {
          if (!entry.symptoms_date) {
            return;
          }

          return (
            <React.Fragment>
              <Moment format={'ddd ' + dateFormat}>{entry.symptoms_date}</Moment>
            </React.Fragment>
          );
        },
      },
      {
        key: 'result',
        headingText: props.t('Result'),
        content: (entry) => {
          return (
            <React.Fragment>
              {capitalizeFirstLetter(entry.result)}
            </React.Fragment>
          );
        },
      },
      {
        key: 'note',
        headingText: props.t('Note'),
        content: (entry) => {
          return (
            <React.Fragment>
              {entry.note}
            </React.Fragment>
          );
        },
      },
    ];

    if (props.selectedPupil.can_record_covid_tests) {
      this.columns.push({
        key: 'actions',
        headingText: props.t('Actions'),
        content: (entry) => {
          if (!entry.can_delete) {
            return null;
          }

          return (
            <React.Fragment>
              <Button
                color="inherit"
                size="small"
                onClick={this.onDeleteClick.bind(this, entry)}
                className="delete-button"
              >
                {props.t('Delete')}
              </Button>
            </React.Fragment>
          );
        },
      });
    }

    this.onTableHeadingClick = this.onTableHeadingClick.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
    this.getFinalEntries = this.getFinalEntries.bind(this);
  }

  onTableHeadingClick(column) {
    const {
      sort,
      sortDirection,
    } = this.state;

    if (column.key === 'actions') {
      return;
    }

    let newSort = column.key;
    let newSortDirection = sortDirection;

    if (!newSortDirection) {
      newSortDirection = 'asc';
    }

    if (newSort === sort) {
      newSortDirection = newSortDirection === 'asc'
        ? 'desc'
        : 'asc';
    }

    this.setState({
      sort: newSort,
      sortDirection: newSortDirection,
    });
  }

  onDeleteClick(entry) {
    const {
      t,
      enqueueSnackbar,
    } = this.props;

    confirm(t('Are you sure you wish to remove this report?')).then(
      () => {
        axios
          .post(
            `${window.apiUrl}/deletecovidtest/${entry.id}`,
            {},
            {
              cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
            }
          ).then((response) => {
            if (response.data.error) {
              enqueueSnackbar(response.data.error, {
                variant: 'error',
                autoHideDuration: 2000,
              });
            } else {
              enqueueSnackbar(t('You have successfully deleted the COVID test'), {
                variant: 'success',
                autoHideDuration: 2000,
              });

              this.props.reloadData();
            }
          }).catch((thrown) => {});
      },
      () => {
        // Nothing to do
      }
    );
  }

  getFinalEntries(entries) {
    const {
      sort,
      sortDirection,
    } = this.state;

    let finalEntries = [ ...entries ];

    finalEntries.sort((a, b) => {
      if (
        sort === 'test_date' ||
        sort === 'symptoms_date'
      ) {
        const aDate = new Date(a[sort] ?? '2000-01-01');
        const bDate = new Date(b[sort] ?? '2000-01-01');

        return aDate - bDate;
      }

      const aSort = a[sort] ?? '';
      const bSort = b[sort] ?? '';

      return aSort.localeCompare(bSort);
    });

    if (sortDirection === 'desc') {
      finalEntries = finalEntries.reverse();
    }

    return finalEntries;
  }

  render() {
    const {
      classes,
      entries,
    } = this.props;
    const {
      sort,
      sortDirection,
    } = this.state;

    const finalEntries = this.getFinalEntries(entries);

    return (
      <div className={`covid-tests-table ${classes.root}`}>
        <table className={classes.table}>
          <thead>
            <tr>
              {this.columns.map((column) => {
                let columnProps = {};
                if (column.headerWidth) {
                  columnProps.width = column.headerWidth;
                }

                return (
                  <th
                    { ...columnProps }
                    key={column.key}
                    onClick={this.onTableHeadingClick.bind(this, column)}
                  >
                    <div className={classes.tableHeadingText}>
                      <b>{column.headingText}</b>
                      {(column.key !== sort || !sortDirection) && column.key !== 'actions' && (
                        <span>
                          <SwapVertIcon className={classes.textMute} fontSize="small" />
                        </span>
                      )}
                      {column.key === sort && sortDirection && column.key !== 'actions' && (
                        <span>
                          {sortDirection === 'asc' && (
                            <ArrowUpwardIcon fontSize="small" />
                          )}
                          {sortDirection === 'desc' && (
                            <ArrowDownwardIcon fontSize="small" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {finalEntries.map((entry) => {
              return (
                <tr key={entry.id}>
                  {this.columns.map((column) => {
                    return (
                      <td key={column.key}>
                        {column.content.bind(this, entry)()}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

export default connect(mapStateToProps)(
  withStyles(styles)(withTranslation()(withSnackbar(CovidTestsTable)))
);
